// src/com/trans_plan_tsr/TransPlanTsrEditSupplyExpress.js
import React, { useEffect, useState } from 'react';
import { Box } from '@mui/system';
import { styled } from '@mui/material/styles';
import { TableContainer, Table, TableHead, TableBody, TableCell, TableRow } from '@mui/material';
import { AppWord, AppStyle, CodeGroup } from '../../value/AppConst';
import VzmIntField from '../../common/VzmIntField';
import VzmSelector from '../../common/VzmSelector';
import ValueMisc from '../../value/ValueMisc';
import { codeTreeRepo } from '../../value/AppModel';
import { useReactiveVar } from '@apollo/client';


const TitleCellBg = '#e0d8f2';

const TemperTitleCell = styled(TableCell)({
    backgroundColor: '#e3fffd',
    color: AppStyle.PrimaryRGB,
    fontWeight: 'bold',
    textAlign: 'center',
});

const TitleCell = styled(TableCell)({
    backgroundColor: TitleCellBg,
    color: AppStyle.PrimaryRGB,
    fontWeight: 'bold',
    textAlign: 'center',
});

const WoodTitleCell = styled(TableCell)({
    backgroundColor: '#e0e3ca',
    fontWeight: 'bold',
    textAlign: 'center',
});

const SubTitleCell = styled(TableCell)({
    backgroundColor: TitleCellBg,
    fontWeight: 'bold',
    textAlign: 'center',
});

const TemperatureValueKey = 'temperature';

const DataDict = {
    tempF: {id: 'tempF', valueKey:TemperatureValueKey}, // 전면(온도)
    eFDolly: {id: 'eFDolly', check: (value) => value >=0 && value <= 999999}, // Dolly fore
    eFDollycart: {id: 'eFDollycart', check: (value) => value >=0 && value <= 999999}, // Cart fore
    eFRoll: {id: 'eFRoll', check: (value) => value >=0 && value <= 999999}, // Roll Cage fore
    eFPalKpp: {id: 'eFPalKpp', check: (value) => value >=0 && value <= 999999}, // Pallet KPP fore
    eFPalAju: {id: 'eFPalAju', check: (value) => value >=0 && value <= 999999}, // Pallet AJU fore
    tempM: {id: 'tempM', valueKey:TemperatureValueKey}, // 중면(온도)
    eMDolly: {id: 'eMDolly', check: (value) => value >=0 && value <= 999999}, // Dolly middle
    eMDollycart: {id: 'eMDollycart', check: (value) => value >=0 && value <= 999999}, // Cart middle
    eMRoll: {id: 'eMRoll', check: (value) => value >=0 && value <= 999999}, // Roll Cage middle
    eMPalKpp: {id: 'eMPalKpp', check: (value) => value >=0 && value <= 999999}, // Pallet KPP middle
    eMPalAju: {id: 'eMPalAju', check: (value) => value >=0 && value <= 999999}, // Pallet AJU middle
    tempR: {id: 'tempR', valueKey:TemperatureValueKey}, // 후면(온도)
    eRDolly: {id: 'eRDolly', check: (value) => value >=0 && value <= 999999}, // Dolly rear
    eRDollycart: {id: 'eRDollycart', check: (value) => value >=0 && value <= 999999}, // Cart rear
    eRRoll: {id: 'eRRoll', check: (value) => value >=0 && value <= 999999}, // Roll Cage rear
    eRPalKpp: {id: 'eRPalKpp', check: (value) => value >=0 && value <= 999999}, // Pallet KPP rear
    eRPalAju: {id: 'eRPalAju', check: (value) => value >=0 && value <= 999999}, // Pallet AJU rear
    eCigar: {id: 'eCigar', check: (value) => value >=0 && value <= 999999}, // 담배
    eCorn: {id: 'eCorn', check: (value) => value >=0 && value <= 999999}, // 양곡
    ePulp: {id: 'ePulp', check: (value) => value >=0 && value <= 999999}, // 제지
    eMu: {id: 'eMu', check: (value) => value >=0 && value <= 999999}, // MU
    eJumDolly: {id: 'eJumDolly', check: (value) => value >=0 && value <= 999999}, // Pallet Dolly
    eJumDollycart: {id: 'eJumDollycart', check: (value) => value >=0 && value <= 999999}, // Pallet Cart
    eJumRoll: {id: 'eJumRoll', check: (value) => value >=0 && value <= 999999}, // Pallet Roll Cage
};

const WORD_OFF = 'off';

export default function TransPlanTsrEditSupplyExpress({
    item,
    onChangeValue
}) {
    const [temperatureOptions, setTemperatureOptions] = useState([WORD_OFF]);
    const codeTree = useReactiveVar(codeTreeRepo);

    useEffect(()=>{
        setTemperatureOptions(
            ValueMisc.sortTemperatureCodeOptions(
                ValueMisc.makeCodesToSelectList(codeTree, CodeGroup.TEMPERATURE_CD, TemperatureValueKey))
            );
    }, [codeTree]);

    return(
        <Box>
            <TableContainer>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TemperTitleCell rowSpan={2} size={AppWord.SMALL}>&nbsp;</TemperTitleCell>
                            <TemperTitleCell rowSpan={2} size={AppWord.SMALL}>온도</TemperTitleCell>
                            <TitleCell rowSpan={2} size={AppWord.SMALL}>Dolly</TitleCell>
                            <TitleCell rowSpan={2} size={AppWord.SMALL}>Cart</TitleCell>
                            <TitleCell rowSpan={2} size={AppWord.SMALL}>Roll Cage</TitleCell>
                            <TitleCell colSpan={2} size={AppWord.SMALL}>Pallet</TitleCell>
                        </TableRow>
                        <TableRow>
                            <SubTitleCell size={AppWord.SMALL}>KPP</SubTitleCell>
                            <SubTitleCell size={AppWord.SMALL}>AJU</SubTitleCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <TableRow>
                            <TemperTitleCell>전면</TemperTitleCell>
                            <TableCell>
                                <VzmSelector
                                    dict={DataDict.tempF}
                                    value={item.tempF || WORD_OFF}
                                    list={temperatureOptions}
                                    onChange={onChangeValue}
                                />
                            </TableCell>
                            <TableCell>
                                <VzmIntField
                                    dict={DataDict.eFDolly} value={item.eFDolly}
                                    onChange={onChangeValue} fullWidth={true}
                                />
                            </TableCell>
                            <TableCell>
                                <VzmIntField
                                    dict={DataDict.eFDollycart} value={item.eFDollycart}
                                    onChange={onChangeValue} fullWidth={true}
                                />
                            </TableCell>
                            <TableCell>
                                <VzmIntField
                                    dict={DataDict.eFRoll} value={item.eFRoll}
                                    onChange={onChangeValue} fullWidth={true}
                                />
                            </TableCell>
                            <TableCell>
                                <VzmIntField
                                    dict={DataDict.eFPalKpp} value={item.eFPalKpp}
                                    onChange={onChangeValue} fullWidth={true}
                                />
                            </TableCell>
                            <TableCell>
                                <VzmIntField
                                    dict={DataDict.eFPalAju} value={item.eFPalAju}
                                    onChange={onChangeValue} fullWidth={true}
                                />
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TemperTitleCell>중면</TemperTitleCell>
                            <TableCell>
                                <VzmSelector
                                    dict={DataDict.tempM}
                                    value={item.tempM || WORD_OFF}
                                    list={temperatureOptions}
                                    onChange={onChangeValue}
                                />
                            </TableCell>
                            <TableCell>
                                <VzmIntField
                                    dict={DataDict.eMDolly} value={item.eMDolly}
                                    onChange={onChangeValue} fullWidth={true}
                                />
                            </TableCell>
                            <TableCell>
                                <VzmIntField
                                    dict={DataDict.eMDollycart} value={item.eMDollycart}
                                    onChange={onChangeValue} fullWidth={true}
                                />
                            </TableCell>
                            <TableCell>
                                <VzmIntField
                                    dict={DataDict.eMRoll} value={item.eMRoll}
                                    onChange={onChangeValue} fullWidth={true}
                                />
                            </TableCell>
                            <TableCell>
                                <VzmIntField
                                    dict={DataDict.eMPalKpp} value={item.eMPalKpp}
                                    onChange={onChangeValue} fullWidth={true}
                                />
                            </TableCell>
                            <TableCell>
                                <VzmIntField
                                    dict={DataDict.eMPalAju} value={item.eMPalAju}
                                    onChange={onChangeValue} fullWidth={true}
                                />
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TemperTitleCell>후면</TemperTitleCell>
                            <TableCell>
                                <VzmSelector
                                    dict={DataDict.tempR}
                                    value={item.tempR || WORD_OFF}
                                    list={temperatureOptions}
                                    onChange={onChangeValue}
                                />
                            </TableCell>
                            <TableCell>
                                <VzmIntField
                                    dict={DataDict.eRDolly} value={item.eRDolly}
                                    onChange={onChangeValue} fullWidth={true}
                                />
                            </TableCell>
                            <TableCell>
                                <VzmIntField
                                    dict={DataDict.eRDollycart} value={item.eRDollycart}
                                    onChange={onChangeValue} fullWidth={true}
                                />
                            </TableCell>
                            <TableCell>
                                <VzmIntField
                                    dict={DataDict.eRRoll} value={item.eRRoll}
                                    onChange={onChangeValue} fullWidth={true}
                                />
                            </TableCell>
                            <TableCell>
                                <VzmIntField
                                    dict={DataDict.eRPalKpp} value={item.eRPalKpp}
                                    onChange={onChangeValue} fullWidth={true}
                                />
                            </TableCell>
                            <TableCell>
                                <VzmIntField
                                    dict={DataDict.eRPalAju} value={item.eRPalAju}
                                    onChange={onChangeValue} fullWidth={true}
                                />
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>
            <Box style={{height:20}}></Box>
            <TableContainer>
                <Table>
                    <TableHead>
                        <TableRow>
                            <WoodTitleCell rowSpan={2} size={AppWord.SMALL}>담배</WoodTitleCell>
                            <WoodTitleCell rowSpan={2} size={AppWord.SMALL}>양곡</WoodTitleCell>
                            <WoodTitleCell rowSpan={2} size={AppWord.SMALL}>제지</WoodTitleCell>
                            <WoodTitleCell rowSpan={2} size={AppWord.SMALL}>MU</WoodTitleCell>
                            <WoodTitleCell colSpan={3} size={AppWord.SMALL}>Pallet</WoodTitleCell>
                        </TableRow>
                        <TableRow>
                            <WoodTitleCell size={AppWord.SMALL}>Dolly</WoodTitleCell>
                            <WoodTitleCell size={AppWord.SMALL}>Cart</WoodTitleCell>
                            <WoodTitleCell size={AppWord.SMALL}>Roll Cage</WoodTitleCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <TableRow>
                            <TableCell>
                                <VzmIntField
                                    dict={DataDict.eCigar} value={item.eCigar}
                                    onChange={onChangeValue} fullWidth={true}
                                />
                            </TableCell>
                            <TableCell>
                                <VzmIntField
                                    dict={DataDict.eCorn} value={item.eCorn}
                                    onChange={onChangeValue} fullWidth={true}
                                />
                            </TableCell>
                            <TableCell>
                                <VzmIntField
                                    dict={DataDict.ePulp} value={item.ePulp}
                                    onChange={onChangeValue} fullWidth={true}
                                />
                            </TableCell>
                            <TableCell>
                                <VzmIntField
                                    dict={DataDict.eMu} value={item.eMu}
                                    onChange={onChangeValue} fullWidth={true}
                                />
                            </TableCell>
                            <TableCell>
                                <VzmIntField
                                    dict={DataDict.eJumDolly} value={item.eJumDolly}
                                    onChange={onChangeValue} fullWidth={true}
                                />
                            </TableCell>
                            <TableCell>
                                <VzmIntField
                                    dict={DataDict.eJumDollycart} value={item.eJumDollycart}
                                    onChange={onChangeValue} fullWidth={true}
                                />
                            </TableCell>
                            <TableCell>
                                <VzmIntField
                                    dict={DataDict.eJumRoll} value={item.eJumRoll}
                                    onChange={onChangeValue} fullWidth={true}
                                />
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );    
}